import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useWeb3 } from '../../context/Web3Context';
import { useToast } from '../../context/ToastContext';
import CONTRACTS from '../../contracts/addresses';

const AMTS = [10, 20, 30, 50, 100, 200, 300, 500, 1000];

export default function RegisterPage() {
  const { contracts, account, setIsRegistered, setUserId } = useWeb3();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [sponsor, setSponsor] = useState(params.get('ref') || '9999');
  const [pos, setPos] = useState(parseInt(params.get('pos')) || 0);
  const posLocked = params.get('pos') !== null;
  const [amount, setAmount] = useState('10');
  const [custom, setCustom] = useState('');
  const [usdtBal, setUsdtBal] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contracts.usdt || !account) return;
    contracts.usdt.balanceOf(account).then(b => setUsdtBal(b)).catch(() => {});
  }, [contracts.usdt, account]);

  const finalAmt = custom || amount;

  const handleRegister = async () => {
    if (!finalAmt || parseFloat(finalAmt) < 10) { toast.error('Minimum $10'); return; }
    if (parseFloat(finalAmt) % 10 !== 0) { toast.error('Multiples of $10'); return; }
    setLoading(true);
    try {
      const wei = ethers.utils.parseUnits(finalAmt.toString(), 18);
      const allowance = await contracts.usdt.allowance(account, CONTRACTS.STORAGE);
      if (allowance.lt(wei)) {
        toast.info('Approving USDT...');
        const atx = await contracts.usdt.approve(CONTRACTS.STORAGE, ethers.constants.MaxUint256);
        await atx.wait();
        toast.success('Approved!');
      }
      toast.info('Registering...');
      const tx = await contracts.logic.registerAndInvest(parseInt(sponsor) || 9999, pos, wei);
      await tx.wait();
      const uid = await contracts.storage.getUserIdByAddress(account);
      const id = uid.toNumber();
      setUserId(id); setIsRegistered(true);
      // Navigate to home with welcome flag
      navigate(`/home?welcome=true&userId=${id}&amount=${finalAmt}`);
    } catch (e) { toast.error(e.reason || 'Registration failed'); }
    setLoading(false);
  };

  return (<motion.div className="page" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
    <motion.div style={{ textAlign: 'center', marginBottom: 24 }} initial={{ scale: 0.8 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}>
      <img src="/logo.png" alt="RF" style={{ width: 160, marginBottom: 16, filter: 'drop-shadow(0 0 30px rgba(255,184,0,0.2))' }} />
      <h2 style={{ fontFamily: 'var(--font-h)', fontSize: '1.4rem', fontWeight: 800 }}>Join Rich Factory</h2>
      <p style={{ color: 'var(--text2)', fontSize: '0.82rem', marginTop: 4 }}>Start your wealth manufacturing journey</p>
    </motion.div>

    <motion.div className="conic-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
      <div className="fg"><label className="fl">Sponsor ID</label><input type="number" value={sponsor} onChange={e => setSponsor(e.target.value)} placeholder="Sponsor ID" /></div>
      <div className="fg"><label className="fl">Position {posLocked && <span style={{ fontSize: '0.65rem', color: 'var(--cyan)', marginLeft: 6 }}>🔒 Set by referrer</span>}</label>
        <div className="g2">
          <motion.div className={`pos-btn ${pos === 0 ? 'on' : ''} ${posLocked ? 'locked' : ''}`} onClick={() => !posLocked && setPos(0)} whileTap={posLocked ? {} : { scale: 0.95 }} style={posLocked ? { opacity: pos === 0 ? 1 : 0.4, cursor: 'not-allowed' } : {}}>◀ LEFT</motion.div>
          <motion.div className={`pos-btn ${pos === 1 ? 'on' : ''} ${posLocked ? 'locked' : ''}`} onClick={() => !posLocked && setPos(1)} whileTap={posLocked ? {} : { scale: 0.95 }} style={posLocked ? { opacity: pos === 1 ? 1 : 0.4, cursor: 'not-allowed' } : {}}>RIGHT ▶</motion.div>
        </div>
      </div>
      <div className="fg"><label className="fl">Investment Amount (USDT)</label>
        <div className="g3">{AMTS.map(a => (
          <motion.div key={a} className={`amt-btn ${!custom && amount === String(a) ? 'on' : ''}`}
            onClick={() => { setAmount(String(a)); setCustom(''); }} whileTap={{ scale: 0.93 }}>${a}</motion.div>
        ))}</div>
      </div>
      <div className="fg"><input type="number" placeholder="Custom amount (multiples of $10)" value={custom} onChange={e => { setCustom(e.target.value); setAmount(''); }} /></div>
      {usdtBal && <div className="fb" style={{ marginBottom: 14 }}>
        <span style={{ fontSize: '0.78rem', color: 'var(--text2)' }}>USDT Balance</span>
        <span style={{ fontFamily: 'var(--font-m)', fontSize: '0.82rem', color: 'var(--cyan)' }}>${parseFloat(ethers.utils.formatUnits(usdtBal, 18)).toFixed(2)}</span>
      </div>}
      <motion.button className="btn btn-gold btn-full" onClick={handleRegister} disabled={loading} style={{ padding: 16, fontSize: '1rem' }}
        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
        {loading ? '⏳ Processing...' : `🚀 Register & Invest $${finalAmt}`}
      </motion.button>
    </motion.div>
  </motion.div>);
}
